import React, { useMemo, useState, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { User, Download, QrCode, FileText } from 'lucide-react';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import logo from '../assets/logo.webp'

const IDCard = () => {
  const [cardSide, setCardSide] = useState('front');
  const [imageLoadError, setImageLoadError] = useState(false);
  const [signatureLoadError, setSignatureLoadError] = useState(false);
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const { showToast } = useToast();
  const frontRef = useRef(null);
  const backRef = useRef(null);
  const downloadContainerRef = useRef(null);



  useEffect(() => {
    const fetchStudentData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError('No authentication token found');
          setLoading(false);
          return;
        }

        console.log('Fetching data from:', `${process.env.REACT_APP_API_URL}api/get-swimming-records`);

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);

        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}api/get-swimming-records`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            signal: controller.signal
          });

          clearTimeout(timeoutId);

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const result = await response.json();
          console.log('API Response:', result);
          
          if (result.status === 'success' && result.data && result.data.length > 0) {
            setStudentData(result.data[0]);
            setLoading(false);
            return;
          } else {
            setStudentData(null);
            setError('No student records found');
          }
        } catch (fetchError) {
          clearTimeout(timeoutId);
          console.error('Fetch error:', fetchError);
          
          if (fetchError.name === 'AbortError') {
            setError('Request timeout - server not responding');
          } else {
            setError(`Fetch failed: ${fetchError.message}`);
          }
        }

      } catch (err) {
        console.error('General error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchStudentData();

  }, []);

  const cardData = useMemo(() => {
    if (studentData) {
      // Handle both API response formats
      let imageUrl = studentData.photo_applicant || studentData.image || '';
      if (imageUrl && !imageUrl.startsWith('data:')) {
        // Clean up malformed paths returned by some API configurations
        imageUrl = imageUrl.replace(/localhost\/(localhost\/)+/g, 'localhost/');
        imageUrl = imageUrl.replace(/localhost\/http:\/\/localhost\//g, 'localhost/');
        
        const apiUrl = process.env.REACT_APP_API_URL || '';
        if (imageUrl.includes('localhost') && apiUrl && !apiUrl.includes('localhost')) {
          const apiBase = new URL(apiUrl).origin;
          imageUrl = imageUrl.replace(/^https?:\/\/localhost/, apiBase);
        }
        imageUrl = imageUrl.replace(/ /g, '%20').replace(/\(/g, '%28').replace(/\)/g, '%29');
      }

      let signatureUrl = studentData.sign_applicant || '';
      if (signatureUrl && !signatureUrl.startsWith('data:')) {
        const apiUrl = process.env.REACT_APP_API_URL || '';
        if (signatureUrl.includes('localhost') && apiUrl && !apiUrl.includes('localhost')) {
          const apiBase = new URL(apiUrl).origin;
          signatureUrl = signatureUrl.replace(/^https?:\/\/localhost/, apiBase);
        }
        signatureUrl = signatureUrl.replace(/ /g, '%20').replace(/\(/g, '%28').replace(/\)/g, '%29');
      }

      return {
        name: studentData.application_name || studentData.fullname || 'Student',
        designation: studentData.category?.category || studentData.category || 'Student',
        idNo: studentData.registration_no || 'N/A',
        phone: studentData.mobile_no || studentData.phone || 'Not Provided',
        email: studentData.email || 'Not Provided',
        session: studentData.session?.session || studentData.session || 'Not Provided',
        shift: studentData.shift?.shift_name || studentData.shift || 'Not Provided',
        time: studentData.time_slot?.time || studentData.time || 'Not Provided',
        joiningDate: studentData.registration_date ? new Date(studentData.registration_date).toLocaleDateString('en-GB') : 
                    (studentData.created_at ? new Date(studentData.created_at).toLocaleDateString('en-GB') : new Date().toLocaleDateString('en-GB')),
        expiryDate: studentData.expiry_date ? new Date(studentData.expiry_date).toLocaleDateString('en-GB') : new Date().toLocaleDateString('en-GB'),
        brandName: 'Swimming Pool',
        tagline: 'Your Swimming Journey Starts Here',
        department: 'Aquatics Department',
        photoUrl: imageUrl,
        signatureUrl: signatureUrl,
        category: studentData.category
      };
    }

    // Fallback to user data if API data not available
    const now = new Date();
    const defaultJoining = now.toLocaleDateString('en-GB');
    const defaultExpiry = new Date(
      now.getFullYear() + 1,
      now.getMonth(),
      now.getDate()
    ).toLocaleDateString('en-GB');

    return {
      name: user?.name || 'Student',
      designation: user?.role ? `${user.role.charAt(0).toUpperCase()}${user.role.slice(1)}` : 'Student',
      idNo: user?.id || 'N/A',
      phone: user?.phone || user?.mobile || 'Not Provided',
      email: user?.email || 'Not Provided',
      joiningDate: user?.joiningDate || defaultJoining,
      expiryDate: user?.expiryDate || defaultExpiry,
      brandName: 'Swimming Pool',
      tagline: 'Your Swimming Journey Starts Here',
      department: 'Aquatics Department',
      photoUrl: user?.photo || user?.profileImage || user?.avatar || user?.image || '',
      signatureUrl: '',
      category: null
    };
  }, [studentData, user]);
 
  // Add another useMemo to handle Base64 conversion for downloads
  const [capturePhotoUrl, setCapturePhotoUrl] = useState('');
  
  useEffect(() => {
    let isMounted = true;
    if (!cardData.photoUrl || cardData.photoUrl.startsWith('data:')) return;

    // Use an Image object instead of fetch to avoid the "red screen" CORS error overlay
    const img = new Image();
    img.crossOrigin = 'anonymous'; 
    
    img.onload = () => {
      if (!isMounted) return;
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL('image/png');
        setCapturePhotoUrl(dataURL);
      } catch (err) {
        console.warn("Canvas capture failed (CORS):", err.message);
      }
    };

    img.onerror = () => {
      console.log("Note: Image could not be converted to Base64 for download due to security restrictions.");
    };

    img.src = cardData.photoUrl;

    return () => { isMounted = false; };
  }, [cardData.photoUrl]);

  const showPhoto = Boolean(cardData.photoUrl) && !imageLoadError;

  const handleImageError = (e) => {
    console.log('Image failed to load:', cardData.photoUrl);
    console.log('Original image URLs:', {
      photo_applicant: studentData?.photo_applicant,
      image: studentData?.image
    });
    setImageLoadError(true);
  };

  const handleSignatureError = (e) => {
    console.log('Signature failed to load:', cardData.signatureUrl);
    console.log('Original signature URL:', studentData?.sign_applicant);
    setSignatureLoadError(true);
  };

  const handleDownloadPDF = async () => {
    try {
      showToast('Generating PDF...', 'info');
      
      const front = frontRef.current;
      const back = backRef.current;
      
      if (!front || !back) {
        showToast('Card elements not found', 'error');
        return;
      }

      // Capture front side
      const canvasFront = await html2canvas(front, {
        useCORS: true,
        scale: 3,
        logging: false,
        backgroundColor: null
      });
      
      // Capture back side
      const canvasBack = await html2canvas(back, {
        useCORS: true,
        scale: 3,
        logging: false,
        backgroundColor: null
      });

      const imgDataFront = canvasFront.toDataURL('image/png');
      const imgDataBack = canvasBack.toDataURL('image/png');

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      
      // Calculate dimensions (keeping aspect ratio)
      const imgWidth = 120; // Slightly larger for better readability
      const imgHeightFront = (canvasFront.height * imgWidth) / canvasFront.width;
      const imgHeightBack = (canvasBack.height * imgWidth) / canvasBack.width;
      
      const x = (pageWidth - imgWidth) / 2;
      
      // One single page layout
      pdf.setFontSize(20);
      pdf.setTextColor(10, 60, 150);
      pdf.text('BALURGHAT MUNICIPALITY', pageWidth / 2, 20, { align: 'center' });
      
      pdf.setFontSize(14);
      pdf.setTextColor(80, 80, 80);
      pdf.text('Swimming Pool Identity Card', pageWidth / 2, 28, { align: 'center' });
      
      // Front Side
      pdf.setFontSize(11);
      pdf.text('FRONT SIDE', pageWidth / 2, 45, { align: 'center' });
      pdf.addImage(imgDataFront, 'PNG', x, 50, imgWidth, imgHeightFront);
      
      // Back Side (neatly spaced below)
      const gap = 20;
      const backY = 50 + imgHeightFront + gap;
      
      pdf.text('BACK SIDE', pageWidth / 2, backY - 5, { align: 'center' });
      pdf.addImage(imgDataBack, 'PNG', x, backY, imgWidth, imgHeightBack);

      // Line separators for professional look
      pdf.setDrawColor(200);
      pdf.line(20, 35, pageWidth - 20, 35);

      pdf.save(`${cardData.name.replace(/\s+/g, '_')}_ID_Card.pdf`);
      showToast('PDF downloaded successfully', 'success');
    } catch (err) {
      console.error('PDF generation error:', err);
      showToast('Failed to generate PDF', 'error');
    }
  };

  const handleDownloadImage = async () => {
    try {
      showToast('Generating Image...', 'info');
      
      const container = downloadContainerRef.current;
      if (!container) {
        showToast('Card container not found', 'error');
        return;
      }

      const canvas = await html2canvas(container, {
        useCORS: true,
        scale: 2,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `${cardData.name.replace(/\s+/g, '_')}_ID_Card.png`;
      link.href = imgData;
      link.click();
      
      showToast('Image downloaded successfully', 'success');
    } catch (err) {
      console.error('Image generation error:', err);
      showToast('Failed to generate image', 'error');
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <Header />
        <div className="dashboard-wrapper">
          <Sidebar activeItem="idcard" />
          <div className="flex-grow-1 dashboard-content">
            <div className="text-center mt-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading student data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !error.includes('No student records found')) {
    return (
      <div className="dashboard-container">
        <Header />
        <div className="dashboard-wrapper">
          <Sidebar activeItem="idcard" />
          <div className="flex-grow-1 dashboard-content">
            <div className="alert alert-danger mt-3" role="alert">
              <strong>Error:</strong> {error}
              <button 
                className="btn btn-sm btn-outline-danger ms-3" 
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if no student data is available, if there was an error about no records, or if payment is not completed
  if (!studentData || (error && error.includes('No student records found')) || (studentData.payment_status === "0")) {
    return (
      <div className="dashboard-container">
        <Header />
        <div className="dashboard-wrapper">
          <Sidebar activeItem="idcard" />
          <div className="flex-grow-1 dashboard-content">
            <div className="text-center mt-5">
              <div className="alert alert-info mt-3" role="alert">
                <h4 className="alert-heading">Sorry, ID card not generated yet</h4>
                <p className="mb-0">
                  {studentData?.payment_status === "0" 
                    ? "Your payment is pending. Please complete the payment to generate your ID card."
                    : "Your ID card hasn't been generated yet. Please contact administration if you think this is an error."
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Header />
      <div className="dashboard-wrapper">
        <Sidebar activeItem="idcard" />
        <div className="flex-grow-1 dashboard-content">
          <h2 className="page-title">Download ID Card</h2>

          <div className="row justify-content-center">
            <div className="col-lg-8">
              {/* ID Card Preview */}
              <div className="id-card-container mb-4">
                <div className="text-center mb-3">
                  <div className="btn-group" role="group">
                    <button
                      type="button"
                      className={`btn ${cardSide === 'front' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setCardSide('front')}
                    >
                      Front Side
                    </button>
                    <button
                      type="button"
                      className={`btn ${cardSide === 'back' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setCardSide('back')}
                    >
                      Back Side
                    </button>
                  </div>
                </div>

                <div className="id-card-wrapper">
                  {/* Front of ID Card */}
                  {cardSide === 'front' && (
                    <div className={`identity-card ${new Date().getHours() >= 6 && new Date().getHours() < 18 ? 'day' : 'night'}`}>
                      <div className="card-header">
                        <div className="logo-section">
                          <div className="circular-logo">
                           <img src={logo} alt="Logo" width={60} height={60}/> 
                          </div>
                        </div>
                        <div className="header-text">
                          <h1 className="municipality-title">BALURGHAT MUNICIPALITY</h1>
                          <h2 className="pool-title">SWIMMING POOL</h2>
                          <h3 className="card-title">IDENTITY CARD</h3>
                        </div>
                        <div className="photo-box">
                          {showPhoto ? (
                            <img
                              src={cardData.photoUrl}
                              alt={`${cardData.name} profile`}
                              className="card-photo"
                              onError={handleImageError}
                            />
                          ) : (
                            <div className="photo-placeholder-box">
                              <span className="photo-text">Photo</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="card-details-section">
                        <div className="details-table">
                          <div className="detail-row">
                            <span className="detail-label">Age:</span>
                            <span className="detail-value">{studentData?.date_of_birth ? new Date().getFullYear() - new Date(studentData.date_of_birth).getFullYear() + ' yrs' : '12 - 30 yrs'}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Time:</span>
                            <span className="detail-value">{cardData.time}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Session:</span>
                            <span className="detail-value">{cardData.session}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Category:</span>
                            <span className="detail-value">{cardData?.category?.category}</span>
                          </div>
                        </div>
                      </div>

                      <div className="personal-info-section">
                        <div className="info-row">
                          <span className="info-label">Name:</span>
                          <span className="info-value">{cardData.name}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Name of Father/Guardian:</span>
                          <span className="info-value">{studentData?.name_of_father || '_________________________'}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Address:</span>
                        </div>
                        <div className="address-lines">
                          <div className="address-line">{studentData?.residential_address || '_________________________'}</div>
                          <div className="address-line">Ward No: {studentData?.ward_no || '_________________________'}</div>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Mob.:</span>
                          <span className="info-value">{cardData.phone}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">D.O.B.:</span>
                          <span className="info-value">{studentData?.date_of_birth ? new Date(studentData.date_of_birth).toLocaleDateString('en-GB') : '_________________________'}</span>
                        </div>
                      </div>

                      <div className="card-footer">
                        <div className="signature-section">
                          <span className="chairman-label">Chairman</span>
                          <span className="municipality-label">Balurghat Municipality</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Back of ID Card */}
                  {cardSide === 'back' && (
                    <div className={`id-card-back ${new Date().getHours() >= 6 && new Date().getHours() < 18 ? 'day' : 'night'}`}>
                      <div className="id-card-back-header">
                        <h3 className="id-card-brand">{cardData.brandName}</h3>
                      </div>

                      <div className="id-card-back-body">
                        <div className="instructions-section">
                          <h4 className="instructions-title">INSTRUCTIONS:</h4>
                          <ul className="instructions-list">
                            <li>This card is property of {cardData.brandName}</li>
                            <li>Please keep this card safe at all times</li>
                            <li>Report loss immediately to authorities</li>
                            <li>This card is non-transferable</li>
                            <li>Valid for swimming pool access only</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Download Options */}
              <div className="card">
                <div className="card-body">
                  <h5 className="card-title mb-3">Download Options</h5>
                  <div className="row">
                    <div className="col-md-6 mb-2">
                      <button className="btn btn-primary w-100" onClick={handleDownloadPDF}>
                        <Download size={16} className="me-2" />
                        Download as PDF
                      </button>
                    </div>
                    <div className="col-md-6 mb-2">
                      <button className="btn btn-outline-primary w-100" onClick={handleDownloadImage}>
                        <FileText size={16} className="me-2" />
                        Download as Image
                      </button>
                    </div>
                  </div>

                  <div className="mt-3">
                    <div className="row small text-muted">
                      <div className="col-6">
                        <p className="mb-1"><strong>Card Type:</strong> Student ID</p>
                        <p className="mb-1"><strong>Status:</strong> Active</p>
                      </div>
                      <div className="col-6">
                        <p className="mb-1"><strong>Issued:</strong> {cardData.joiningDate}</p>
                        <p className="mb-1"><strong>Expires:</strong> {cardData.expiryDate}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden container for downloads */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0, zIndex: -1 }}>
        <div ref={downloadContainerRef} style={{ padding: '20px', background: '#f8f9fa' }}>
          {/* Front Side */}
          <div ref={frontRef} style={{ marginBottom: '30px' }}>
            <div className={`identity-card ${new Date().getHours() >= 6 && new Date().getHours() < 18 ? 'day' : 'night'}`}>
              <div className="card-header">
                <div className="logo-section">
                  <div className="circular-logo">
                    <img src={logo} alt="Logo" width={60} height={60} /> 
                  </div>
                </div>
                <div className="header-text">
                  <h1 className="municipality-title">BALURGHAT MUNICIPALITY</h1>
                  <h2 className="pool-title">SWIMMING POOL</h2>
                  <h3 className="card-title">IDENTITY CARD</h3>
                </div>
                <div className="photo-box">
                  {showPhoto ? (
                    <img
                      src={capturePhotoUrl || cardData.photoUrl}
                      alt={`${cardData.name} profile`}
                      className="card-photo"
                      crossOrigin="anonymous"
                      onError={(e) => console.log('Download image load error')}
                    />
                  ) : (
                    <div className="photo-placeholder-box">
                      <span className="photo-text">Photo</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="card-details-section">
                <div className="details-table">
                  <div className="detail-row">
                    <span className="detail-label">Age:</span>
                    <span className="detail-value">{studentData?.date_of_birth ? new Date().getFullYear() - new Date(studentData.date_of_birth).getFullYear() + ' yrs' : '12 - 30 yrs'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Time:</span>
                    <span className="detail-value">{cardData.time}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Session:</span>
                    <span className="detail-value">{cardData.session}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Category:</span>
                    <span className="detail-value">{cardData?.category?.category}</span>
                  </div>
                </div>
              </div>

              <div className="personal-info-section">
                <div className="info-row">
                  <span className="info-label">Name:</span>
                  <span className="info-value">{cardData.name}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Name of Father/Guardian:</span>
                  <span className="info-value">{studentData?.name_of_father || '_________________________'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Address:</span>
                </div>
                <div className="address-lines">
                  <div className="address-line">{studentData?.residential_address || '_________________________'}</div>
                  <div className="address-line">Ward No: {studentData?.ward_no || '_________________________'}</div>
                </div>
                <div className="info-row">
                  <span className="info-label">Mob.:</span>
                  <span className="info-value">{cardData.phone}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">D.O.B.:</span>
                  <span className="info-value">{studentData?.date_of_birth ? new Date(studentData.date_of_birth).toLocaleDateString('en-GB') : '_________________________'}</span>
                </div>
              </div>

              <div className="card-footer">
                <div className="signature-section">
                  <span className="chairman-label">Chairman</span>
                  <span className="municipality-label">Balurghat Municipality</span>
                </div>
              </div>
            </div>
          </div>

          {/* Back Side */}
          <div ref={backRef}>
            <div className={`id-card-back ${new Date().getHours() >= 6 && new Date().getHours() < 18 ? 'day' : 'night'}`}>
              <div className="id-card-back-header">
                <h3 className="id-card-brand">{cardData.brandName}</h3>
              </div>

              <div className="id-card-back-body">
                <div className="instructions-section">
                  <h4 className="instructions-title">INSTRUCTIONS:</h4>
                  <ul className="instructions-list">
                    <li>This card is property of {cardData.brandName}</li>
                    <li>Please keep this card safe at all times</li>
                    <li>Report loss immediately to authorities</li>
                    <li>This card is non-transferable</li>
                    <li>Valid for swimming pool access only</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IDCard;
